import { HttpErrorResponse } from "@angular/common/http";
import { formatPersonNameLastFirst } from "@app/shared/util/person-name";
import { CalendarEvent } from "angular-calendar";
import {
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

/**
 * Titles for the EZ notification (ngx-toastr) raised when a scheduling action is blocked. Shared so
 * the drag/drop calendar path, the Schedule dialog, and the Reschedule dialog all raise the SAME
 * titled toast — every scheduling rejection looks identical wherever it originates.
 */
export const SCHEDULE_BLOCKED_TOAST_TITLE = "Cannot schedule";
export const RESCHEDULE_BLOCKED_TOAST_TITLE = "Cannot reschedule";

/** Resolved terminology strings the schedule/reschedule write-error mapper folds into its message. */
export interface ScheduleWriteErrorText {
  permissionDenied: string;
  conflict: string;
  failedTitle: string;
  failed: string;
}

/**
 * Builds the single-line message for a failed schedule/reschedule WRITE, for the EZ notification
 * toast both dialogs raise. Mirrors the mapping the dialogs used before the toast switch: a 403 is
 * the permission text; a field-keyed validation problem is the server's issue detail(s) (a conflict
 * is prefixed with the conflict hint so it reads the same as the drop-path toast); a non-field-keyed
 * HTTP failure (404/500) is "<failed title> (HTTP nnn[: title]). ..." plus any problem detail; a
 * non-HTTP error is the generic failed text. Keeps the specific server reason visible instead of a
 * dead generic message.
 */
export function scheduleWriteErrorMessage(error: unknown, text: ScheduleWriteErrorText): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 403) {
      return text.permissionDenied;
    }

    const problem = error.error as { errors?: Record<string, string[]> } | null;
    const entries = Object.entries(problem?.errors ?? {});
    if (entries.length > 0) {
      const isConflict = entries.some(([key]) => key.toLowerCase().includes("conflict"));
      const details = entries.flatMap(([, messages]) => messages);
      return (isConflict ? [text.conflict, ...details] : details).join(" ");
    }

    const detail = httpProblemDetail(error);
    const base =
      `${text.failedTitle} (${describeHttpFailure(error)}). ` + "Try again or contact support if the problem persists.";
    return detail ? `${base} ${detail}` : base;
  }

  return text.failed;
}

/**
 * One scheduled hearing row from the C3 proceeding-calendar projection
 * (GET /api/v1/scheduling/projections/calendar). Panel and proceeding type are
 * not carried on the calendar list projection; they surface on the hearing-detail
 * (proceeding-detail) projection consumed by the later hearing-detail screen.
 */
export interface HearingSchedulingCalendarContext { requestId: number; revision: number; workflowId: number; occurrenceId: number; eTag: string; dueDate: string; }
export interface ProceedingCalendarRow {
  schedulingContext?: HearingSchedulingCalendarContext | null;
  scheduledProceedingId: number;
  proceedingNumber: string;
  title: string;
  schedulingStatusCode: string;
  calendarSlotId?: number | null;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
  timeZoneId: string;
  locationName?: string | null;
  roomName?: string | null;
  videoConferenceUrl?: string | null;
  isScheduled: boolean;
  // Incarcerated-person id for a hearing_case subject, so a global-search result can be matched to its
  // scheduled hearing. Surfaced by the calendar projection read (joined from scheduled_proceedings).
  subjectKey?: string | null;
  // Derived past-date lifecycle flag (#1447): true when the scheduled window has fully elapsed and no
  // finalized hearing capture exists for the proceeding ("awaiting hearing input"). Server-derived on
  // every read — never stored — so recording the input clears it on the next fetch. See
  // docs/phms/hearing-past-date-lifecycle.md.
  requiresHearingInput?: boolean;
  // Assigned hearing official (#1531), for the commissioner filter on the schedule toolbar. Resolved
  // server-side on every read from the hearing's official assignment, presiding first — never stored
  // on the projection — so reassigning an official changes the calendar on the next fetch with no
  // projection rebuild. Null when no official is assigned yet.
  assignedOfficialUserId?: number | null;
  assignedOfficialDisplayName?: string | null;
}

export interface HearingScheduleQuery {
  startUtc?: string;
  endUtc?: string;
  statusCode?: string;
}

/**
 * One candidate from the ready-to-schedule projection
 * (GET /api/v1/scheduling/projections/ready-to-schedule). Used by the search bar's Add-to-Calendar
 * flow to place an inmate's pending proceeding onto the calendar. `subjectKey` is the incarcerated
 * person id for a `hearing_case` subject.
 */
export interface ReadyToScheduleRow {
  schedulingRequestId?: number | null;
  schedulingContext?: HearingSchedulingCalendarContext | null;
  scheduledProceedingId: number;
  proceedingNumber: string;
  subjectTypeCode: string;
  subjectKey: string;
  proceedingTypeCode: string;
  title: string;
  schedulingStatusCode: string;
  earliestScheduleStartUtc?: string | null;
  latestScheduleEndUtc?: string | null;
  locationName?: string | null;
  roomName?: string | null;
  eligibilityStatusCode?: string | null;
  eligibilityNextEligibleDate?: string | null;
  eligibilityPendingInformationReasonCodes?: string[] | null;
  eligibilityBlockerReasonCodes?: string[] | null;
  /** Display inmate number ("A-1001") resolved from inmate_profiles; the dashboards' Inmate #. */
  inmateNumber?: string | null;
}

/**
 * Request body for the schedule (add) write endpoint
 * (POST /api/v1/scheduling/projections/proceedings/{id}/schedule). The target window is required;
 * an optional calendar slot binds the hearing to a slot. PHMS-SCH-6.
 */
export interface ScheduleHearingRequest {
  schedulingContext?: HearingSchedulingCalendarContext | null;
  scheduledStartUtc: string;
  scheduledEndUtc: string;
  calendarSlotId?: number | null;
  timeZoneId?: string | null;
  locationName?: string | null;
  roomName?: string | null;
  videoConferenceUrl?: string | null;
  reasonCode?: string | null;
  schedulingNotes?: string | null;
}

/**
 * Result returned by the schedule/unschedule write endpoints (the workflow runtime's
 * EzProceedingScheduleResult). The calendar is refreshed via getCalendar after a successful write,
 * so only the identifying fields are consumed by the UI.
 */
export interface ScheduleHearingResult {
  workflowInstanceId: number;
  scheduledProceedingId: number;
  schedulingPostureCode: string;
  calendarSlotId?: number | null;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
}

export interface HearingScheduleDay {
  dateKey: string;
  dateLabel: string;
  hearings: ProceedingCalendarRow[];
}

export interface HearingScheduleFilters {
  startUtc: string;
  endUtc: string;
  location: string;
  resource: string;
  status: string;
}

export type HearingScheduleSortKey = "dateTime" | "location" | "resource" | "status";
export type HearingScheduleSortDirection = "asc" | "desc";

export interface HearingScheduleSort {
  key: HearingScheduleSortKey;
  direction: HearingScheduleSortDirection;
}

export const EMPTY_HEARING_SCHEDULE_FILTERS: HearingScheduleFilters = {
  startUtc: "",
  endUtc: "",
  location: "",
  resource: "",
  status: "",
};

export const DEFAULT_HEARING_SCHEDULE_SORT: HearingScheduleSort = {
  key: "dateTime",
  direction: "asc",
};

/**
 * Builds the server-side calendar projection query from the selected filters. Only the
 * projection-supported parameters (date range, status) are sent; empty selections are omitted
 * so the request carries only chosen values. Location and resource are applied client-side
 * because the calendar list projection does not filter on them server-side.
 */
export function toScheduleQuery(filters: HearingScheduleFilters): HearingScheduleQuery {
  const query: HearingScheduleQuery = {};
  const startUtc = toIsoStartOfDay(filters.startUtc);
  if (startUtc) {
    query.startUtc = startUtc;
  }
  const endUtc = toIsoEndOfDay(filters.endUtc);
  if (endUtc) {
    query.endUtc = endUtc;
  }
  const status = filters.status.trim();
  if (status) {
    query.statusCode = status;
  }
  return query;
}

export function applyClientFilters(
  rows: readonly ProceedingCalendarRow[],
  filters: HearingScheduleFilters,
): ProceedingCalendarRow[] {
  const location = filters.location.trim().toLowerCase();
  const resource = filters.resource.trim().toLowerCase();

  return rows.filter((row) => {
    if (location && !(row.locationName ?? "").toLowerCase().includes(location)) {
      return false;
    }
    if (resource && !formatResource(row).toLowerCase().includes(resource)) {
      return false;
    }
    return true;
  });
}

export function sortHearings(
  rows: readonly ProceedingCalendarRow[],
  sort: HearingScheduleSort,
): ProceedingCalendarRow[] {
  const direction = sort.direction === "desc" ? -1 : 1;

  return [...rows].sort((left, right) => {
    const primary = compareBySortKey(left, right, sort.key);
    if (primary !== 0) {
      return primary * direction;
    }
    // Stable, deterministic tie-break independent of the active direction.
    return left.proceedingNumber.localeCompare(right.proceedingNumber);
  });
}

function compareBySortKey(
  left: ProceedingCalendarRow,
  right: ProceedingCalendarRow,
  key: HearingScheduleSortKey,
): number {
  switch (key) {
    case "location":
      return (left.locationName ?? "").localeCompare(right.locationName ?? "");
    case "resource":
      return formatResource(left).localeCompare(formatResource(right));
    case "status":
      return left.schedulingStatusCode.localeCompare(right.schedulingStatusCode);
    case "dateTime":
    default:
      return startEpoch(left) - startEpoch(right);
  }
}

function startEpoch(row: ProceedingCalendarRow): number {
  if (!row.scheduledStartUtc) {
    return Number.MAX_SAFE_INTEGER;
  }
  const time = new Date(row.scheduledStartUtc).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

function toIsoStartOfDay(value: string): string | undefined {
  return toIsoBoundary(value, false);
}

function toIsoEndOfDay(value: string): string | undefined {
  return toIsoBoundary(value, true);
}

function toIsoBoundary(value: string, endOfDay: boolean): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  // Date-only input (yyyy-mm-dd) is expanded to a day boundary; full timestamps pass through.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const candidate = dateOnly
    ? new Date(`${trimmed}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`)
    : new Date(trimmed);
  return Number.isNaN(candidate.getTime()) ? undefined : candidate.toISOString();
}

/** One issue code and its detail from a ValidationProblem `errors` map (#1526). */
export interface HearingScheduleErrorIssue {
  code: string;
  message: string;
}

export interface HearingScheduleDisplayError {
  title: string;
  messages: string[];
  /**
   * The issue code beside each detail from a ValidationProblem `errors` map (one entry per code per
   * message), so a banner can name the specific server rule that refused the write (#1526). Empty
   * for failures that carry no keyed errors.
   */
  issues: HearingScheduleErrorIssue[];
  statusCode?: number;
}

const UNSCHEDULED_DATE_KEY = "unscheduled";

export function groupHearingsByDate(rows: readonly ProceedingCalendarRow[]): HearingScheduleDay[] {
  const days = new Map<string, HearingScheduleDay>();

  for (const row of rows) {
    const dateKey = toDateKey(row.scheduledStartUtc);
    const day = days.get(dateKey) ?? {
      dateKey,
      dateLabel: toDateLabel(row.scheduledStartUtc),
      hearings: [],
    };
    day.hearings.push(row);
    days.set(dateKey, day);
  }

  return [...days.values()]
    .map((day) => ({
      ...day,
      hearings: [...day.hearings].sort(compareByStart),
    }))
    .sort((left, right) => {
      if (left.dateKey === UNSCHEDULED_DATE_KEY) {
        return 1;
      }
      if (right.dateKey === UNSCHEDULED_DATE_KEY) {
        return -1;
      }
      return left.dateKey.localeCompare(right.dateKey);
    });
}

export function formatResource(row: ProceedingCalendarRow): string {
  const parts = [row.roomName?.trim(), row.videoConferenceUrl?.trim() ? "Video" : null]
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" / ") : "—";
}

export function formatScheduledTime(value: string | null | undefined): string {
  if (!value) {
    return "Unscheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(date);
}

export function mapHearingScheduleError(error: unknown): HearingScheduleDisplayError {
  if (error instanceof HttpErrorResponse) {
    const statusCode = error.status;
    if (statusCode === 401) {
      return {
        title: "Sign-in required",
        messages: ["Your session is no longer authenticated. Sign in again to view the hearing schedule."],
        issues: [],
        statusCode,
      };
    }
    if (statusCode === 403) {
      return {
        title: "Access denied",
        messages: ["You do not have permission to view the hearing schedule."],
        issues: [],
        statusCode,
      };
    }
    // A field-keyed validation/conflict problem (a placement rule violation from the schedule/
    // reschedule/unschedule write paths) carries the SPECIFIC rule messages in its errors map
    // keyed by issue code. Surface those verbatim (ADO 1483/1486) instead of collapsing them into
    // the generic title/detail, so the flyout move-date and add-hearing banners explain exactly
    // which rule blocked the placement (lead time, weekend, past date, the conflicting booking).
    const problem = error.error as { title?: string; errors?: Record<string, string[]> } | null;
    const errorEntries = Object.entries(problem?.errors ?? {});
    if (errorEntries.length > 0) {
      const isConflict = errorEntries.some(([key]) => key.toLowerCase().includes("conflict"));
      return {
        title: isConflict ? "Scheduling conflict" : (problem?.title ?? "Schedule change failed"),
        messages: errorEntries.flatMap(([, entryMessages]) => entryMessages),
        issues: errorEntries.flatMap(([code, entryMessages]) => entryMessages.map((message) => ({ code, message }))),
        statusCode,
      };
    }
    // Carry the HTTP status and any server problem-details title/detail into the banner so a
    // failure is self-diagnosing instead of a generic dead end (the old fixed message hid the
    // underlying cause entirely, making a load 500 impossible to triage from the UI).
    const messages = [
      `The hearing schedule could not be loaded (${describeHttpFailure(error)}). `
      + "Try again or contact support if the problem persists.",
    ];
    const detail = httpProblemDetail(error);
    if (detail) {
      messages.push(detail);
    }
    return {
      title: "Schedule unavailable",
      messages,
      issues: [],
      statusCode,
    };
  }

  return {
    title: "Unexpected error",
    messages: ["The hearing schedule could not be loaded. Try again or contact support if the problem persists."],
    issues: [],
  };
}

/**
 * Compact diagnostic core for a failed schedule HTTP call: "HTTP <status>[: <problem title>]",
 * or a network-unreachable note for status 0. Shared by the calendar banner and the eligible-
 * backlog strip so every load/write failure surfaces enough to diagnose the next occurrence.
 */
export function describeHttpFailure(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return "network error — the scheduling API could not be reached";
  }
  const title = problemDetailsOf(error)?.title?.trim();
  return title ? `HTTP ${error.status}: ${title}` : `HTTP ${error.status}`;
}

/** The server problem-details `detail` line when one is present and adds information. */
export function httpProblemDetail(error: HttpErrorResponse): string | null {
  const problem = problemDetailsOf(error);
  const detail = problem?.detail?.trim();
  if (!detail || detail === problem?.title?.trim()) {
    return null;
  }
  return detail;
}

/** The RFC-7807 body of a failed response, when the server sent one. */
function problemDetailsOf(error: HttpErrorResponse): { title?: string; detail?: string } | null {
  const payload: unknown = error.error;
  if (payload && typeof payload === "object") {
    return payload as { title?: string; detail?: string };
  }
  return null;
}

function compareByStart(left: ProceedingCalendarRow, right: ProceedingCalendarRow): number {
  const leftTime = toEpoch(left.scheduledStartUtc);
  const rightTime = toEpoch(right.scheduledStartUtc);
  if (leftTime !== rightTime) {
    return leftTime - rightTime;
  }
  return left.proceedingNumber.localeCompare(right.proceedingNumber);
}

function toEpoch(value: string | null | undefined): number {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

/**
 * Local-calendar-day key (yyyy-mm-dd) for List-view grouping. Built from LOCAL date parts — not
 * `toISOString()` — so grouping agrees with the day labels (rendered in local time by Intl) and
 * with the Month/Week/Day calendar views, which all place events on local days. With the old UTC
 * key, an evening hearing near the local/UTC date boundary grouped under a different day than the
 * one its own label (and every calendar view) showed it on.
 */
function toDateKey(value: string | null | undefined): string {
  if (!value) {
    return UNSCHEDULED_DATE_KEY;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return UNSCHEDULED_DATE_KEY;
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function toDateLabel(value: string | null | undefined): string {
  if (!value) {
    return "Unscheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Unscheduled"
    : new Intl.DateTimeFormat(undefined, { dateStyle: "full" }).format(date);
}

// ---------------------------------------------------------------------------
// angular-calendar integration helpers (shared with the Month/#1317 and
// Week/#1318 views established by the SCH-1 shell).
// ---------------------------------------------------------------------------

/**
 * Shape of a calendar-event color block. angular-calendar consumes `EventColor`
 * from `calendar-utils` but does not re-export that name, so we mirror the
 * structural type here for the readiness palette helpers.
 */
export interface CalendarEventColor {
  primary: string;
  secondary: string;
  secondaryText?: string;
}

export type HearingReadiness = "ready" | "prep" | "docsMissing" | "scheduled";

export interface ReadinessLegendItem {
  readiness: HearingReadiness;
  label: string;
  color: CalendarEventColor;
}

/**
 * Placeholder readiness palette. Mason finalizes the canonical tokens in SCH-2;
 * keep all color decisions funnelled through `readinessColor`/`READINESS_LEGEND`
 * so the later story can swap the values in one place.
 */
const READINESS_COLORS: Record<HearingReadiness, CalendarEventColor> = {
  ready: { primary: "#1b873f", secondary: "#e6f4ea" }, // green
  prep: { primary: "#b25e09", secondary: "#fdf0e1" }, // amber
  docsMissing: { primary: "#c62828", secondary: "#fdecea" }, // red
  scheduled: { primary: "#1565c0", secondary: "#e7f0fb" }, // blue
};

export const READINESS_LEGEND: readonly ReadinessLegendItem[] = [
  { readiness: "ready", label: "Ready", color: READINESS_COLORS.ready },
  { readiness: "prep", label: "Prep in Progress", color: READINESS_COLORS.prep },
  { readiness: "docsMissing", label: "Docs Missing", color: READINESS_COLORS.docsMissing },
  { readiness: "scheduled", label: "Scheduled", color: READINESS_COLORS.scheduled },
];

/**
 * Classifies a row's readiness. DOCUMENTED PLACEHOLDER PROXY: until Mason
 * finalizes the readiness model in SCH-2 we derive readiness from the existing
 * `schedulingStatusCode`. Isolated here so the proxy can be replaced wholesale.
 */
export function readinessOf(row: ProceedingCalendarRow): HearingReadiness {
  const code = (row.schedulingStatusCode ?? "").trim().toLowerCase();
  if (code.includes("ready")) {
    return "ready";
  }
  if (code.includes("prep") || code.includes("progress")) {
    return "prep";
  }
  if (code.includes("missing") || code.includes("docs") || code.includes("incomplete")) {
    return "docsMissing";
  }
  return "scheduled";
}

export function readinessColor(row: ProceedingCalendarRow): CalendarEventColor {
  return READINESS_COLORS[readinessOf(row)];
}

/** Chip label for a past-date hearing awaiting its input (#1447). Shared across surfaces. */
export const NEEDS_HEARING_INPUT_LABEL = "Needs hearing input";

/** Hover/tooltip wording explaining the overdue state and where the click leads (#1447). */
export const NEEDS_HEARING_INPUT_HINT =
  "This hearing's scheduled date has passed without recorded hearing input. Open the hearing input capture to record the outcome.";

/**
 * Overdue "needs hearing input" palette (#1447): amber chip with dark amber text so past-date
 * hearings stand out from the readiness colors without reading as an error.
 */
export const NEEDS_HEARING_INPUT_COLOR: CalendarEventColor = {
  primary: "#b45309",
  secondary: "#fef3c7",
  secondaryText: "#7c2d12",
};

/**
 * True when the row is awaiting hearing input (#1447): its scheduled window elapsed with no
 * finalized hearing capture. Reads the server-derived flag only — the client never re-derives it,
 * so every surface (schedule, list, dashboard) agrees with the projection read.
 */
export function needsHearingInput(row: ProceedingCalendarRow | null | undefined): boolean {
  return row?.requiresHearingInput === true;
}

/**
 * Maps a calendar projection row to an angular-calendar event. Title combines
 * the candidate name with the scheduled time; `meta` carries the original row
 * for click-through; color comes from the readiness proxy — except for a
 * past-date hearing awaiting its input (#1447), which takes the overdue amber
 * palette so the Week/Day event blocks and Month chip dots flag it too.
 */
export function mapRowToEvent(row: ProceedingCalendarRow): CalendarEvent<ProceedingCalendarRow> {
  const start = row.scheduledStartUtc ? new Date(row.scheduledStartUtc) : new Date(NaN);
  const end = row.scheduledEndUtc ? new Date(row.scheduledEndUtc) : undefined;
  // Drop an end that is missing, invalid, or not after the start so the calendar
  // does not warn about inverted ranges.
  const validEnd = end && !Number.isNaN(end.getTime()) && end > start ? end : undefined;
  const time = formatScheduledTime(row.scheduledStartUtc);
  return {
    id: row.scheduledProceedingId,
    start,
    end: validEnd,
    // Display-only event label ("Last, First Middle" per #1436); `meta` keeps the raw row.
    title: `${formatPersonNameLastFirst(row.title)} · ${time}`,
    color: needsHearingInput(row) ? NEEDS_HEARING_INPUT_COLOR : readinessColor(row),
    meta: row,
  };
}

/**
 * Maps the visible rows into angular-calendar events, skipping rows that have no
 * (or an invalid) scheduled start since the calendar views cannot place them.
 */
export function toCalendarEvents(
  rows: readonly ProceedingCalendarRow[],
): CalendarEvent<ProceedingCalendarRow>[] {
  const events: CalendarEvent<ProceedingCalendarRow>[] = [];
  for (const row of rows) {
    if (!row.scheduledStartUtc) {
      continue;
    }
    const start = new Date(row.scheduledStartUtc);
    if (Number.isNaN(start.getTime())) {
      continue;
    }
    events.push(mapRowToEvent(row));
  }
  return events;
}

/** Default business-hours window for the Week/Day time grids (9am–5pm). */
export const DEFAULT_DAY_START_HOUR = 9;
export const DEFAULT_DAY_END_HOUR = 21;

/**
 * Start hour for the Week/Day time grids: the business-hours default, widened downward when an
 * event starts earlier. angular-calendar DROPS events that fall entirely outside the displayed
 * dayStart–dayEnd window from the Week/Day grids, while the Month view shows every event on its
 * day — so a fixed 9–5 window made early/late hearings appear in Month but silently vanish from
 * Week/Day. Deriving the window from the visible events guarantees all three views show the same
 * hearings while keeping the compact 9–5 grid whenever everything fits in business hours.
 */
export function gridStartHour(
  events: readonly CalendarEvent<ProceedingCalendarRow>[],
  defaultStartHour: number = DEFAULT_DAY_START_HOUR,
): number {
  let start = defaultStartHour;
  for (const event of events) {
    if (!Number.isNaN(event.start.getTime())) {
      start = Math.min(start, event.start.getHours());
    }
  }
  return Math.max(start, 0);
}

/**
 * End hour for the Week/Day time grids: the business-hours default, widened upward when an event
 * starts or ends later (the grid renders through the end of this hour). See gridStartHour for why
 * the window must cover every visible event.
 */
export function gridEndHour(
  events: readonly CalendarEvent<ProceedingCalendarRow>[],
  defaultEndHour: number = DEFAULT_DAY_END_HOUR,
): number {
  let end = defaultEndHour;
  for (const event of events) {
    if (!Number.isNaN(event.start.getTime())) {
      end = Math.max(end, event.start.getHours());
    }
    if (event.end && !Number.isNaN(event.end.getTime())) {
      end = Math.max(end, event.end.getHours());
    }
  }
  return Math.min(end, 23);
}

/**
 * Visible-range helpers. Each returns an ISO `{ startUtc, endUtc }` window for
 * the active angular-calendar view around `viewDate`, suitable for
 * `HearingScheduleService.getCalendar`. The Month range is padded out to whole
 * weeks so the month grid's leading/trailing days are populated.
 */
export interface CalendarRange {
  startUtc: string;
  endUtc: string;
}

export function dayRange(viewDate: Date): CalendarRange {
  return { startUtc: startOfDay(viewDate).toISOString(), endUtc: endOfDay(viewDate).toISOString() };
}

export function weekRange(viewDate: Date): CalendarRange {
  return {
    startUtc: startOfWeek(viewDate).toISOString(),
    endUtc: endOfWeek(viewDate).toISOString(),
  };
}

export function monthRange(viewDate: Date): CalendarRange {
  return {
    startUtc: startOfWeek(startOfMonth(viewDate)).toISOString(),
    endUtc: endOfWeek(endOfMonth(viewDate)).toISOString(),
  };
}

/**
 * Quick-count windows for the shell chips, derived from `now` (defaults to the
 * current time). "This week" is the calendar week containing now; "next week" is
 * the following calendar week.
 */
export function isToday(value: string | null | undefined, now: Date = new Date()): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date >= startOfDay(now) && date <= endOfDay(now);
}

export function isThisWeek(value: string | null | undefined, now: Date = new Date()): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date >= startOfWeek(now) && date <= endOfWeek(now);
}

export function isNextWeek(value: string | null | undefined, now: Date = new Date()): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  const next = addWeeks(now, 1);
  return !Number.isNaN(date.getTime()) && date >= startOfWeek(next) && date <= endOfWeek(next);
}

/**
 * True when `value` falls within the calendar month of `viewDate`. Used by the
 * Month-view summary so the "N hearings / N Ready" count covers exactly the named
 * month, excluding the leading/trailing days the grid borrows from neighbouring
 * months (which `monthRange` still fetches so their chips render).
 */
export function isInMonth(value: string | null | undefined, viewDate: Date): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  return (
    !Number.isNaN(date.getTime()) && date >= startOfMonth(viewDate) && date <= endOfMonth(viewDate)
  );
}

/** Month + year label for the Month-view header (e.g. "May 2026"). */
export function monthLabel(viewDate: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(viewDate);
}

/**
 * True when `value` falls within the calendar week of `viewDate` (the same
 * startOfWeek…endOfWeek window `weekRange` fetches). Mirrors `isInMonth` for the
 * Week-view summary so its count covers exactly the displayed week.
 */
export function isInWeek(value: string | null | undefined, viewDate: Date): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  return (
    !Number.isNaN(date.getTime()) && date >= startOfWeek(viewDate) && date <= endOfWeek(viewDate)
  );
}

/**
 * True when `value` falls within the calendar day of `viewDate` (the same
 * startOfDay…endOfDay window `dayRange` fetches). Mirrors `isInWeek`/`isInMonth`
 * for the Day-view summary so its count covers exactly the displayed day.
 */
export function isInDay(value: string | null | undefined, viewDate: Date): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date >= startOfDay(viewDate) && date <= endOfDay(viewDate);
}

/** Full date label for the Day-view header (e.g. "Monday, June 15, 2026"). */
export function dayLabel(viewDate: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "full" }).format(viewDate);
}

/**
 * Range label for the Week-view header (e.g. "May 31 – Jun 6, 2026"). Mirrors
 * `monthLabel`; collapses a shared year and shared month where possible.
 */
export function weekLabel(viewDate: Date): string {
  const start = startOfWeek(viewDate);
  const end = endOfWeek(viewDate);
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const startFmt = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endFmt = new Intl.DateTimeFormat(undefined, {
    month: sameMonth ? undefined : "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startFmt.format(start)} – ${endFmt.format(end)}`;
}
